<template>
  <div class="consultant-popup-mobile">
    <div ref="triggerRef" class="trigger-container" @click="handleTriggerClick">
      <slot name="trigger">
        <button class="default-trigger-btn">
          <span>专属客服</span>
        </button>
      </slot>
    </div>

    <MobilePopup
      v-model:visible="visible"
      :background-color="popupBackgroundColor"
      @close="closePopup"
    >
      <div class="drawer-body">
        <div ref="consultantRef" class="consultant-container">
          <Consultant />
        </div>

        <div class="drawer-actions">
          <div class="action-btn share-btn" @click="shareToWechat">
            <div class="action-btn__icon">
              <a-spin v-show="shareLoading" :size="24" />
              <img v-show="!shareLoading" :src="withBase('/images/icons/share.svg')" alt="分享" />
            </div>
            <span>{{ shareLoading ? '生成中...' : '转发' }}</span>
          </div>
          <div class="action-btn save-btn" @click="saveToLocal">
            <div class="action-btn__icon">
              <a-spin v-show="saveLoading" :size="24" />
              <img v-show="!saveLoading" :src="withBase('/images/icons/download.svg')" alt="保存" />
            </div>
            <span>{{ saveLoading ? '保存中...' : '保存图片' }}</span>
          </div>
        </div>
      </div>
    </MobilePopup>

    <Teleport to="body">
      <Transition name="share-overlay-fade">
        <div v-if="shareOverlayVisible" class="share-overlay" @click.self="closeShareOverlay">
          <div class="share-overlay__content">
            <p class="share-overlay__tip">长按图片可分享给朋友</p>
            <img
              v-if="shareOverlayUrl"
              :src="shareOverlayUrl"
              alt="专属顾问"
              class="share-overlay__image"
            />
            <p class="share-overlay__sub">若无法长按分享，请保存后手动发送</p>
            <button class="share-overlay__close" @click="closeShareOverlay">关闭</button>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
/**
 * @description 移动端专属客服弹窗组件
 * @reason 使用 Canvas API 手绘替代 html2canvas，彻底解决 iOS Safari 上 html2canvas 卡死的问题
 * @emits close - 弹窗关闭时触发
 */

import { ref } from 'vue'
import { BaseMessage } from '~/utils/baseMessage'
import Consultant from './Consultant.vue'
import MobilePopup from '~/components/mobile/Popup.vue'
import { THEME_COLORS, THEME_CSS_VARS } from '~/utils/theme'

const emit = defineEmits(['close'])

const { withBase } = useBasePath()

const consultantRef = ref(null)
const triggerRef = ref(null)

const visible = ref(false)
const MAX_DRAWER_HEIGHT = 620
const drawerHeight = ref(MAX_DRAWER_HEIGHT)

const shareLoading = ref(false)
const saveLoading = ref(false)
const cachedBlobUrl = ref('')
const shareOverlayVisible = ref(false)
const shareOverlayUrl = ref('')
const popupBackgroundColor = THEME_CSS_VARS.bgSoft

const updateDrawerHeight = () => {
  if (!import.meta.client) return
  const vh = window?.innerHeight ?? MAX_DRAWER_HEIGHT
  const next = typeof vh === 'number' && vh > 0 ? vh : MAX_DRAWER_HEIGHT
  drawerHeight.value = Math.min(MAX_DRAWER_HEIGHT, next)
}

onMounted(() => {
  updateDrawerHeight()
  if (!import.meta.client) return
  window.addEventListener('resize', updateDrawerHeight, { passive: true })
})

onUnmounted(() => {
  if (!import.meta.client) return
  window.removeEventListener('resize', updateDrawerHeight)
  if (cachedBlobUrl.value) {
    URL.revokeObjectURL(cachedBlobUrl.value)
  }
})

const handleTriggerClick = () => {
  visible.value = !visible.value
}

const closePopup = () => {
  visible.value = false
  emit('close')
}

const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

const generateImage = async () => {
  if (cachedBlobUrl.value) return cachedBlobUrl.value

  const scale = 2
  const W = 340 * scale
  const H = 420 * scale
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')

  ctx.fillStyle = THEME_COLORS.bgSurface
  ctx.fillRect(0, 0, W, H)

  ctx.fillStyle = THEME_COLORS.textBodyStrong
  ctx.font = `bold ${20 * scale}px -apple-system, "PingFang SC", "Helvetica Neue", sans-serif`
  ctx.textAlign = 'center'
  ctx.fillText('腾达商城专属顾问', W / 2, 44 * scale)

  ctx.fillStyle = THEME_COLORS.textSecondaryWeak
  ctx.font = `${14 * scale}px -apple-system, "PingFang SC", "Helvetica Neue", sans-serif`
  ctx.fillText('免费获取 多重权益', W / 2, 68 * scale)

  try {
    const qrImg = await loadImage(withBase('/images/contact_me_qr_20260821.png'))
    const qrSize = 180 * scale
    const qrX = (W - qrSize) / 2
    const qrY = 88 * scale
    ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize)
  } catch {
    ctx.fillStyle = THEME_COLORS.bgPage
    const qrSize = 180 * scale
    ctx.fillRect((W - qrSize) / 2, 88 * scale, qrSize, qrSize)
    ctx.fillStyle = THEME_COLORS.textCaption
    ctx.font = `${12 * scale}px -apple-system, "PingFang SC", sans-serif`
    ctx.fillText('二维码加载失败', W / 2, (88 + 90) * scale)
  }

  const labels = ['专属顾问', '方案支持', '避坑指南', '工具赋能']
  const iconPaths = [
    '/images/icons/user_gray.svg',
    '/images/icons/design_services.svg',
    '/images/icons/explore.svg',
    '/images/icons/home_repair_service.svg',
  ]
  const startY = 300 * scale
  const iconSize = 36 * scale
  const colWidth = W / 4

  for (let i = 0; i < 4; i++) {
    const cx = colWidth * i + colWidth / 2

    ctx.fillStyle = THEME_COLORS.bgPage
    ctx.beginPath()
    ctx.arc(cx, startY, (iconSize + 12 * scale) / 2, 0, Math.PI * 2)
    ctx.fill()

    try {
      const icon = await loadImage(withBase(iconPaths[i]))
      ctx.drawImage(icon, cx - iconSize / 2, startY - iconSize / 2, iconSize, iconSize)
    } catch {
      // SVG 加载失败时跳过图标绘制
    }

    ctx.fillStyle = THEME_COLORS.textSecondaryWeak
    ctx.font = `${12 * scale}px -apple-system, "PingFang SC", "Helvetica Neue", sans-serif`
    ctx.textAlign = 'center'
    ctx.fillText(labels[i], cx, startY + (iconSize + 12 * scale) / 2 + 16 * scale)
  }

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          resolve(null)
          return
        }
        const blobUrl = URL.createObjectURL(blob)
        cachedBlobUrl.value = blobUrl
        resolve(blobUrl)
      },
      'image/png',
      1
    )
  })
}

const downloadImage = async () => {
  if (!cachedBlobUrl.value) return

  const response = await fetch(cachedBlobUrl.value)
  const blob = await response.blob()
  const blobUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.download = '专属顾问.png'
  link.href = blobUrl
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
}

const closeShareOverlay = () => {
  shareOverlayVisible.value = false
}

const shareToWechat = async () => {
  if (!import.meta.client || shareLoading.value) return

  shareLoading.value = true
  try {
    const blobUrl = await generateImage()
    if (!blobUrl) {
      BaseMessage.error('生成图片失败，请稍后重试')
      return
    }

    const response = await fetch(blobUrl)
    const blob = await response.blob()
    const file = new File([blob], '专属顾问.png', { type: 'image/png' })

    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          title: '专属顾问',
          text: '扫码添加专属顾问，免费获取多重权益',
          files: [file],
        })
        return
      } catch (error) {
        if (error.name === 'AbortError') return
        console.warn('Web Share 失败，回退到图片预览:', error)
      }
    }

    shareOverlayUrl.value = blobUrl
    shareOverlayVisible.value = true
  } catch (error) {
    console.error('转发失败:', error)
    BaseMessage.error('转发失败，请稍后重试')
  } finally {
    shareLoading.value = false
  }
}

const saveToLocal = async () => {
  if (!import.meta.client || saveLoading.value) return

  saveLoading.value = true
  try {
    const blobUrl = await generateImage()
    if (!blobUrl) {
      BaseMessage.error('生成图片失败，请稍后重试')
      return
    }

    await downloadImage()
  } catch (error) {
    console.error('保存失败:', error)
    BaseMessage.error('保存失败，请稍后重试')
  } finally {
    saveLoading.value = false
  }
}

defineExpose({
  shareToWechat,
  saveToLocal,
  closePopup,
})
</script>

<style lang="scss" scoped>
.consultant-popup-mobile {
  position: relative;
  display: inline-block;
  width: 100%;

  .trigger-container {
    cursor: pointer;
    display: inline-block;
    width: 100%;
    height: 100%;

    .default-trigger-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px;
      background-color: $bg-white;
      border: 1px solid $border-light-color;
      border-radius: 24px;
      font-size: 14px;
      color: $text-body-strong-color;
      cursor: pointer;
      transition: all 0.3s ease;

      &:active {
        background-color: $bg-soft-color;
      }

      img {
        width: 18px;
        height: 18px;
      }
    }
  }
}

.drawer-actions {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 12px;

  .action-btn {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    padding: 36px 20px 20px;
    border-radius: 24px;
    font-size: 14px;
    color: $text-body-strong-color;
    cursor: pointer;
    transition: all 0.3s ease;
    min-width: 80px;

    &:active {
      background-color: $bg-soft-color;
    }

    &__icon {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    img {
      width: 40px;
      height: 40px;
    }
  }
}

.drawer-body {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
}

.consultant-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  background-color: $bg-white;
  border-radius: 12px;
}

.share-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 20px;
    max-width: 90vw;
    max-height: 90vh;
  }

  &__tip {
    font-size: 15px;
    color: $bg-white;
    margin: 0 0 16px;
    line-height: 1.6;
    text-align: center;
  }

  &__image {
    max-width: 80vw;
    max-height: 60vh;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    -webkit-touch-callout: default;
  }

  &__sub {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    margin: 16px 0 0;
    line-height: 1.6;
    text-align: center;
  }

  &__close {
    margin-top: 24px;
    padding: 10px 48px;
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 24px;
    background: transparent;
    color: $bg-white;
    font-size: 15px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:active {
      background-color: rgba(255, 255, 255, 0.15);
    }
  }
}

.share-overlay-fade-enter-active,
.share-overlay-fade-leave-active {
  transition: opacity 0.25s ease;
}

.share-overlay-fade-enter-from,
.share-overlay-fade-leave-to {
  opacity: 0;
}
</style>
